import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, ChevronDown } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from 'components/ui/drawer';
import { AvailabilityBadge, PILL } from 'components/AvailabilityBadge';
import { apiErrorMessage } from 'utils/errorMessage';
import { availabilityHint, availabilityLabel, formatPeriod } from 'utils/unavailability';
import { useMyUnavailabilities, useDeleteUnavailability } from 'hooks/useUnavailabilities';
import type { UnavailabilityPeriod } from 'api/unavailabilitiesApi';
import UnavailabilityPeriodList from './UnavailabilityPeriodList';
import DeclareUnavailabilityForm from './DeclareUnavailabilityForm';

/**
 * The second door onto the technician's own availability, opened from the ticket list.
 *
 * `/profile` carries the same thing and is unreachable from the two L'Oréal shells in production:
 * their top bar is native, their four tabs are frozen, and the web entry that leads there is
 * hidden in the WebView. Reaching it from the shell would need a release of binaries those shells
 * will not get, so the way in has to be web-side — which is all this component is.
 *
 * Right of the title by construction: the 50 pt dead zone at the left edge
 * (`allowsBackForwardNavigationGestures` swallowing touches) is fixed in `gps-app` but not in
 * those two shells, which are exactly the population being unblocked.
 */

/** One sheet, three views. Never two stacked drawers: vaul nests badly and a phone has no room. */
type View = 'manage' | 'declare' | 'confirm';

/** The pill's own size, over `AvailabilityBadge`'s default — the design draws 36 px here. */
const PILL_SIZE = 'h-9 gap-2 px-3 text-[13px]';
const COUNT =
  'inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-md bg-zinc-100 px-1.5 text-[11px] font-semibold text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400';
const HINT = 'text-[13px] leading-relaxed text-zinc-500 dark:text-zinc-400';

const MyAvailabilitySheet = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useMyUnavailabilities();
  const remove = useDeleteUnavailability();

  // No loading branch below the pill: the trigger is a Skeleton and not a button while the read
  // is in flight, so the sheet cannot be open then — and an invalidation after a write refetches
  // with data in hand, which never returns `isLoading` to true.
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('manage');
  const [pending, setPending] = useState<UnavailabilityPeriod | null>(null);

  const periods = data?.periods ?? [];
  const availability = data?.availability ?? 'available';
  /**
   * Only a successful read may make a claim. Nothing falls back to "available" when the request
   * failed — a green pill on a failed fetch reads as "you are free", which is the one thing this
   * screen must never say without knowing.
   */
  const loaded = !isLoading && !error;

  const close = () => {
    setOpen(false);
    // A reopened sheet starts on the list, never on the form the previous visit happened to end on.
    setView('manage');
    setPending(null);
  };

  const askDelete = (period: UnavailabilityPeriod) => {
    remove.reset();
    setPending(period);
    setView('confirm');
  };

  const confirmDelete = () => {
    // Guarded as well as disabled: a double tap lands two clicks before the disabled state paints,
    // and a replayed DELETE meets a 404 and reports a failure for a period that is already gone.
    if (!pending || remove.isPending) return;
    remove.mutate(pending.id, {
      onSuccess: () => {
        setPending(null);
        setView('manage');
      },
    });
  };

  const titles: Record<View, { title: string; description: string }> = {
    manage: { title: t('My availability'), description: t('Periods without a new intervention') },
    declare: {
      title: t('Declare an unavailability'),
      description: t('A period without a new intervention'),
    },
    confirm: {
      title: t('Delete this unavailability period?'),
      description: t('This action is immediate'),
    },
  };

  return (
    <>
      {/* The trigger keeps its 44 px box in every state, loading included: a pill that appears
          after the fetch would push the title it sits beside. */}
      <div className="flex min-h-11 shrink-0 items-center">
        {isLoading ? (
          <Skeleton className="h-9 w-32 rounded-full" />
        ) : (
          <button
            type="button"
            onClick={() => setOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={
              loaded ? `${t('My availability')} : ${availabilityLabel(t, availability)}` : t('My availability')
            }
            className="flex items-center rounded-full"
          >
            {loaded ? (
              <AvailabilityBadge state={availability} className={PILL_SIZE}>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </AvailabilityBadge>
            ) : (
              // A failed read claims nothing — and stays tappable, because declaring a period is
              // what the technician came for and the server is what checks the overlap.
              <span
                className={cn(
                  PILL,
                  PILL_SIZE,
                  'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300'
                )}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" aria-hidden="true" />
                {t('My availability')}
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
            )}
          </button>
        )}
      </div>

      <Drawer open={open} onOpenChange={(next) => !next && close()}>
        {/* Same three panes as the profile's sheet, and for the same reason: `overflow-y-auto` on
            `DrawerContent` is inert under vaul's `touch-action: none`, so the scrolling region is
            a child and the actions sit outside it. */}
        <DrawerContent className="mx-auto max-h-[92dvh] w-full overflow-hidden sm:max-w-lg">
          <DrawerHeader className="shrink-0 text-left">
            <DrawerTitle>{titles[view].title}</DrawerTitle>
            <DrawerDescription>{titles[view].description}</DrawerDescription>
          </DrawerHeader>

          {view === 'manage' && (
            <>
              <div className="min-h-0 flex-1 overflow-y-auto">
                <div className="mx-5 flex items-start gap-3 rounded-xl border border-zinc-200 p-3.5 dark:border-zinc-800">
                  {loaded && <AvailabilityBadge state={availability} />}
                  <p className={HINT}>
                    {error
                      ? t(
                          'Your declared periods could not be loaded, so this screen cannot say whether you are available. You can still declare a period.'
                        )
                      : availabilityHint(t, availability, periods)}
                  </p>
                </div>

                <div className="flex items-center gap-2 px-5 pb-2 pt-5">
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
                    {t('My unavailability periods')}
                  </h3>
                  <span className={COUNT}>{loaded ? periods.length : '–'}</span>
                </div>

                <UnavailabilityPeriodList
                  periods={periods}
                  isLoading={isLoading}
                  error={error}
                  onDelete={askDelete}
                />
              </div>

              <div className="shrink-0 border-t border-zinc-200 px-5 pb-8 pt-4 dark:border-zinc-800">
                <Button className="h-12 w-full text-sm" onClick={() => setView('declare')}>
                  <CalendarPlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  {t('Declare an unavailability')}
                </Button>
              </div>
            </>
          )}

          {view === 'declare' && (
            <DeclareUnavailabilityForm
              open={view === 'declare'}
              onClose={() => setView('manage')}
              // Back to the list and not out of the sheet: the period just written is already
              // there, where closing would drop him on the ticket list with nothing to show for it.
              doneLabel={t('Back to my periods')}
              onDone={() => setView('manage')}
            />
          )}

          {view === 'confirm' && pending && (
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-5 pb-8">
              <p className="font-mono text-[15px] font-medium text-zinc-900 dark:text-zinc-50">
                {formatPeriod(pending.startDate, pending.endDate)}
              </p>
              <p className={HINT}>
                {t(
                  'You will be proposed again for the interventions covering these dates. You can declare the period again afterwards.'
                )}
              </p>
              {remove.error && (
                <p className="text-[13px] leading-snug text-rose-600 dark:text-rose-400" role="alert">
                  {apiErrorMessage(t, remove.error)}
                </p>
              )}
              <div className="mt-4 flex gap-2.5">
                <Button
                  variant="outline"
                  className="h-11 flex-1"
                  disabled={remove.isPending}
                  onClick={() => {
                    setPending(null);
                    setView('manage');
                  }}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  variant="destructive"
                  className="h-11 flex-1"
                  disabled={remove.isPending}
                  onClick={confirmDelete}
                >
                  {t('Delete')}
                </Button>
              </div>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MyAvailabilitySheet;
